import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { useImportAssetsModal } from '@/contexts/ImportAssetsModalContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileSpreadsheet, Building2, Search, Upload } from 'lucide-react';
import { downloadCSVTemplate, parseCSV } from '../../../utils/csv-parser';
import { validateAssets } from '../../../utils/asset-validator';
import { getAuthConfig } from '@/lib/auth-fetch';
import { accountingImportService, type FinancialsAssetCategory } from './services/accounting-import.service';
import type { AccountingConnection } from '../../../types/asset-import.types';

type ImportMethod = 'csv' | 'accounting' | 'transaction';

export default function ImportMethodSelection() {
  const navigate = useNavigate();
  const { openModal } = useImportAssetsModal();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedMethod, setSelectedMethod] = useState<ImportMethod>('csv');
  const [connection, setConnection] = useState<AccountingConnection | null>(null);
  const [financialsCategories, setFinancialsCategories] = useState<FinancialsAssetCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  // Accounting import options
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    return date.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [category, setCategory] = useState<string>('all');

  // Get the financials instance ID from auth config
  const getFinancialsInstanceId = (): string => {
    const authConfig = getAuthConfig();
    return authConfig.financialsInstanceId ?? authConfig.instanceId ?? '';
  };

  // Automatically open modal when this page is accessed
  useEffect(() => {
    openModal();
    // Redirect to assets page after opening modal
    navigate('/assets', { replace: true });
  }, [openModal, navigate]);

  useEffect(() => {
    loadConnection();
  }, []);

  const loadConnection = async () => {
    try {
      const financialsInstanceId = getFinancialsInstanceId();
      if (financialsInstanceId) {
        const conn = await accountingImportService.getAccountingConnection(financialsInstanceId);
        setConnection(conn);
        
        // Fetch Financials categories
        try {
          const categories = await accountingImportService.getFinancialsCategories(financialsInstanceId);
          setFinancialsCategories(categories);
        } catch (categoryError) {
          console.error('Failed to load Financials categories:', categoryError);
        }
      } else {
        setConnection(null);
      }
    } catch (error) {
      console.error('Failed to load connection:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    downloadCSVTemplate();
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const processFile = async (file: File) => {
    // Validate file type
    if (!file.name.endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const parseResult = await parseCSV(file);
      const validation = validateAssets(parseResult.data);

      // Navigate to CSV import page with the data
      navigate('/assets/import/csv', {
        state: {
          file: {
            name: file.name,
            size: file.size,
          },
          assets: parseResult.data,
          validation,
        },
      });
    } catch (error) {
      console.error('Failed to parse CSV:', error);
      alert('Failed to parse CSV file. Please check the format and try again.');
    } finally {
      setUploading(false);
      // Reset the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);

    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const handleFetchAssets = async () => {
    setFetching(true);
    try {
      // Navigate to the accounting sync page with the filters
      navigate('/assets/import/accounting-sync', {
        state: {
          filters: {
            dateFrom: new Date(fromDate),
            dateTo: new Date(toDate),
            category: category === 'all' ? 'all' : category
          },
          enableAgentProcessing: true
        }
      });
    } catch (error) {
      console.error('Failed to fetch assets:', error);
    } finally {
      setFetching(false);
    }
  };

  const handleScanTransactions = async () => {
    setFetching(true);
    try {
      // Navigate to the transaction scan page with the filters
      navigate('/assets/import/transaction-scan', {
        state: {
          filters: {
            dateFrom: new Date(fromDate),
            dateTo: new Date(toDate)
          },
          enableAgentProcessing: true
        }
      });
    } catch (error) {
      console.error('Failed to scan transactions:', error);
    } finally {
      setFetching(false);
    }
  };

  const renderAccountingTabContent = () => {
    if (loading) {
      return (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500">Loading connection...</p>
        </div>
      );
    }

    if (connection?.isConnected) {
      return (
        <div className="flex flex-col flex-1">
          <div className="space-y-6 flex-1">
          {/* Connection Status */}
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">{connection.systemName}</p>
                {connection.instanceId && (
                  <p className="text-xs text-slate-500">
                    Instance: {connection.instanceId}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 rounded-full">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              <span className="text-xs font-medium text-green-700">Connected</span>
            </div>
          </div>

          {/* Filter Options */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="space-y-2">
              <Label htmlFor="fromDate" className="font-semibold">From</Label>
              <Input
                id="fromDate"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="toDate" className="font-semibold">To</Label>
              <Input
                id="toDate"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="category" className="font-semibold">Asset Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="category">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {financialsCategories.map((cat) => (
                  <SelectItem key={cat.code} value={cat.code}>
                    {cat.name ?? cat.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          </div>

          {/* Fetch Button - Pinned to bottom */}
          <button
            className="w-full px-6 py-3.5 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 hover:shadow-lg hover:shadow-blue-500/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer mt-6"
            onClick={handleFetchAssets}
            disabled={fetching}
          >
            {fetching ? (
              <>
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                <span>Fetching Assets...</span>
              </>
            ) : (
              <>
                <Search className="w-5 h-5" />
                <span>Fetch Assets</span>
              </>
            )}
          </button>
        </div>
      );
    }

    return (
      <div className="text-center py-16">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 mb-4">
          <Building2 className="h-8 w-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-800 mb-2">No Connection Configured</h3>
        <p className="text-slate-500 mb-6 max-w-md mx-auto">
          Connect your accounting package to import assets automatically.
        </p>
        <button
          className="px-6 py-2.5 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors shadow-lg shadow-blue-500/25 cursor-pointer"
          onClick={() => navigate('/settings/integrations')}
        >
          Configure Integration
        </button>
      </div>
    );
  };

  const renderTransactionTabContent = () => {
    if (loading) {
      return (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-violet-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500">Loading connection...</p>
        </div>
      );
    }

    if (connection?.isConnected) {
      return (
        <div className="flex flex-col flex-1">
          <div className="space-y-6 flex-1">
          {/* Connection Status */}
          <div className="flex items-center justify-between p-4 bg-violet-50 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center">
                <Building2 className="w-5 h-5 text-violet-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">{connection.systemName}</p>
                {connection.lastSyncDate && (
                  <p className="text-xs text-slate-500">
                    Last synced: {new Date(connection.lastSyncDate).toLocaleDateString('en-GB', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 rounded-full">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              <span className="text-xs font-medium text-green-700">Connected</span>
            </div>
          </div>

          {/* Date Filters */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fromDateTx" className="font-semibold">From</Label>
              <Input
                id="fromDateTx"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="toDateTx" className="font-semibold">To</Label>
              <Input
                id="toDateTx"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </div>
          </div>

          {/* Scan Button - Pinned to bottom */}
          <button
            className="w-full px-6 py-3.5 bg-violet-500 text-white rounded-xl font-semibold hover:bg-violet-600 hover:shadow-lg hover:shadow-violet-500/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer mt-6"
            onClick={handleScanTransactions}
            disabled={fetching}
          >
            {fetching ? (
              <>
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                <span>Scanning...</span>
              </>
            ) : (
              <>
                <Search className="w-5 h-5" />
                <span>Scan for Assets</span>
              </>
            )}
          </button>
        </div>
      );
    }

    return (
      <div className="text-center py-16">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 mb-4">
          <Building2 className="h-8 w-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-800 mb-2">No Connection Configured</h3>
        <p className="text-slate-500 mb-6 max-w-md mx-auto">
          Connect your accounting package to scan transactions for assets.
        </p>
        <button
          className="px-6 py-2.5 bg-violet-500 text-white rounded-lg font-medium hover:bg-violet-600 transition-colors shadow-lg shadow-violet-500/25 cursor-pointer"
          onClick={() => navigate('/settings/integrations')}
        >
          Configure Integration
        </button>
      </div>
    );
  };

  return (
    <div className="flex h-full w-full flex-col">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="px-6 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/assets')}
              className="h-8 w-8"
            >
              <Upload className="h-4 w-4" />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold tracking-tight">Import Assets</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Add assets to your register by uploading a file or syncing with your accounting package
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto flex items-center justify-center">
        <div className="mx-auto max-w-4xl p-8 w-full">

          {/* Tab Container */}
          <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 overflow-hidden border border-slate-200">
            {/* Tab Headers */}
            <div className="flex bg-slate-50 p-1.5 gap-1">
              {/* CSV Import Tab */}
              <button
                onClick={() => setSelectedMethod('csv')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-3 rounded-xl font-medium text-xs transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  selectedMethod === 'csv'
                    ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                    : 'text-slate-600 hover:bg-emerald-50 hover:text-emerald-600'
                }`}
              >
                <FileSpreadsheet className="h-4 w-4 flex-shrink-0" />
                <span>CSV Import</span>
              </button>

              {/* Accounting Package Tab */}
              <button
                onClick={() => setSelectedMethod('accounting')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-3 rounded-xl font-medium text-xs transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  selectedMethod === 'accounting'
                    ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                    : 'text-slate-600 hover:bg-blue-50 hover:text-blue-600'
                }`}
              >
                <Building2 className="h-4 w-4 flex-shrink-0" />
                <span>Import From Accounting System</span>
                {connection?.isConnected && (
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${selectedMethod === 'accounting' ? 'bg-green-300' : 'bg-green-500'}`} />
                )}
              </button>

              {/* Transaction Import Tab */}
              <button
                onClick={() => setSelectedMethod('transaction')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-3 rounded-xl font-medium text-xs transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  selectedMethod === 'transaction'
                    ? 'bg-violet-500 text-white shadow-lg shadow-violet-500/30'
                    : 'text-slate-600 hover:bg-violet-50 hover:text-violet-600'
                }`}
              >
                <Search className="h-4 w-4 flex-shrink-0" />
                <span>Import From Transactions</span>
                {connection?.isConnected && (
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${selectedMethod === 'transaction' ? 'bg-green-300' : 'bg-green-500'}`} />
                )}
              </button>
            </div>

            {/* Content Area - Consistent height for all tabs */}
            <div className="p-6 h-[520px] flex flex-col">
              {selectedMethod === 'csv' && (
              <div className="animate-in fade-in duration-200 flex flex-col h-full">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-slate-800 mb-1">Upload Your Asset Data</h3>
                  <p className="text-sm text-slate-500">
                    Import assets from a CSV file with your asset information
                  </p>
                </div>

                {/* Upload Zone - Flexible height */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer mb-4 flex-1 flex flex-col items-center justify-center ${
                    isDragging
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  onClick={handleFileSelect}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  
                  {uploading ? (
                    <div className="space-y-4">
                      <div className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center bg-emerald-100`}>
                        <Upload className="w-6 h-6 text-emerald-500 animate-pulse" />
                      </div>
                      <p className="text-slate-700 font-medium">Processing...</p>
                    </div>
                  ) : (
                    <>
                      <div className={`w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center ${isDragging ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                        <Upload className={`w-6 h-6 ${isDragging ? 'text-emerald-500' : 'text-slate-400'}`} />
                      </div>
                      <p className="text-slate-700 font-medium mb-1">Drag and drop your CSV file here</p>
                      <p className="text-slate-400 text-sm mb-4">or</p>
                      <button 
                        className="px-6 py-2.5 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600 transition-colors shadow-lg shadow-emerald-500/25 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFileSelect();
                        }}
                      >
                        Browse Files
                      </button>
                      <p className="text-xs text-slate-400 mt-4">Accepted format: .csv (max 5MB)</p>
                    </>
                  )}
                </div>

                {/* Template Download */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center">
                      <FileSpreadsheet className="w-5 h-5 text-slate-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700">Need the correct format?</p>
                      <p className="text-xs text-slate-500">Download our template with all required columns</p>
                    </div>
                  </div>
                  <button 
                    className="px-4 py-2 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors cursor-pointer"
                    onClick={handleDownloadTemplate}
                  >
                    Download Template
                  </button>
                </div>
              </div>
              )}

              {selectedMethod === 'accounting' && (
              <div className="animate-in fade-in duration-200 flex flex-col h-full">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-slate-800 mb-1">Import From Accounting System</h3>
                  <p className="text-sm text-slate-500">
                    Fetch existing assets directly from your connected accounting package
                  </p>
                </div>

                {renderAccountingTabContent()}
              </div>
              )}

              {selectedMethod === 'transaction' && (
              <div className="animate-in fade-in duration-200 flex flex-col h-full">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-slate-800 mb-1">Scan Transactions For Assets</h3>
                  <p className="text-sm text-slate-500">
                    Analyse your transaction history to discover and create new assets
                  </p>
                </div>

                {renderTransactionTabContent()}
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
